/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import { ErrorMessage, Field, Formik, FieldArray, getIn } from "formik";
import { Row, Col, Tooltip, Container, OverlayTrigger } from "react-bootstrap";
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import { 
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../resources/validations/validations";
import { useAuthorizedUsersService  } from "../../../services/authorizedUsersService";
import { useLocaleContext } from "../../../contexts/localeContext.js";
import { colors } from "../../../constants/colors";

/* Components */
import { ReactComponent as TrashIcon } from '../../../resources/images/trash-icon.svg';
import { ReactComponent as HelpIcon } from "../../../resources/images/help.svg";
import UCButton from "../../buttons";
import AuthorisedSkeleton from "./skeleton/inviteAuthorisedSkeletonStructure.js"

/* Styles */
import styles from "./styles.js"
import generalStyles from "../../../resources/styles/general";
import AuDeleteWarningModal from "./auDeleteWarningModal";
import {useUserContext} from "../../../contexts/userContext";


const messages = defineMessages({
  namePlaceholderMessage: {
    id: 'inviteauthorisedmodal.name'
  },
  emailPlaceholderMessage: {
    id: 'inviteauthorisedmodal.emailPlaceholder'
  }
})

const Content = props => {
  const { setShowModal, companyEntityId } = props;
  
  const formRef = useRef(null);
  const ArrayHelperRef = useRef()
  const intl = useIntl();
  const globalLocale = useLocaleContext();

  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });

  const { inviteAuthorisedMockedValidationObj } = useValidationService()
  const [testSchema, setTestSchema] = useState({});

  const [shellUsers, setShellUsers] = useState([])
  const [showAuDeleteWarningModal, setShowAuDeleteWarningModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState()
  const userContext = useUserContext();

  const { getShellUsers, inviteAuthorizedUsers, deleteAuthorizedUser } = useAuthorizedUsersService();

  useEffect(() => {
    (async () => {
      const result = createValidationPolicyYupSchema(inviteAuthorisedMockedValidationObj);
      setTestSchema(result)
      //TODO: api call to get validations //simpleMockedApiValidationObj
      if(companyEntityId) {
        const reponse = await getShellUsers(companyEntityId);
        if (reponse) {
          setShellUsers(returnFormData(reponse?.shellUsers))
        }
      }
    })();
  }, []);

  const returnFormData = (formData) => {
    let newArray = []
    formData?.map((item) =>{     
      let data = {}
      data = Object.assign(data, {
        birthdate: item?.attributes?.filter(attribute => attribute.name === "BIRTHDATE")[0]?.value,
        shellUserId: item?.shellUserId,
        name: item?.firstName + " " +  item?.lastName,
        email: ""
      })
      newArray.push(data)
    })
    return newArray
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = (values, setSubmitting) => {
    handleInviteAuthorised(values)
    setSubmitting(false);
  }

  const handleInviteAuthorised = async (values) => {    
    let inviteShellUsersPayload = [];
    const newValues = JSON.parse(JSON.stringify(values))
    newValues.shellUsers && Object.values(newValues.shellUsers).map((item) => {
      delete item["birthdate"]
      delete item["name"]
      if(item.email) {
        inviteShellUsersPayload.push(item)
      }
    })

    if(inviteShellUsersPayload.length > 0) {
      const usersData = {
        companyEntityId: companyEntityId,
        shellUsers: inviteShellUsersPayload
      }
      const response = await inviteAuthorizedUsers(usersData)    

      if (response) {
        handleRemoveInvitedUsers(values, inviteShellUsersPayload) 
        setShowModal(false)
      }
    }
  }
  
  const handleDeleteTeam = async (item, id) => {
    const response = await deleteAuthorizedUser(item)
    if (response) {
      setShowAuDeleteWarningModal(false)
      let updatedShellUsers = [...shellUsers];
      updatedShellUsers = shellUsers.filter(data => data.shellUserId !== item.shellUserId);
      setShellUsers(updatedShellUsers)
      ArrayHelperRef.current.remove(id)
      if(!updatedShellUsers.length) {
        setShowModal(false)
      }
    }
  }

  const handleRemoveInvitedUsers = (values, inviteShellUsersPayload) => {
    let updatedShellUsers = values?.shellUsers;
    updatedShellUsers && Object.values(updatedShellUsers).map((item) => {
      inviteShellUsersPayload && inviteShellUsersPayload.map((shellUser) => {      
        // delete invited shellUsers
        if(item.shellUserId === shellUser.shellUserId) {           
          const index = updatedShellUsers.indexOf(item);
          updatedShellUsers.splice(index, 1)
        }
      })
    })
    setShellUsers(updatedShellUsers)
    if(!updatedShellUsers.length) {
      setShowModal(false)
    }
  }

  return (
    <>
      {
        shellUsers.length > 0 ?
          <div className={classes.modalContent}>
            <Formik
              innerRef={formRef}
              initialValues={{shellUsers}}
              validationSchema={testSchema}
              onSubmit={(values, { setSubmitting }) => {
                submitForm(values, setSubmitting);
              }}
            >
              {({
                  values,
                  errors,
                  touched,
                  setFieldTouched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue
                }) => (
                <>
                  <form onSubmit={handleSubmit} >
                    <Row className={classes.scrollableContainer}>
                      <FieldArray
                        type="shellUsers"
                        name="shellUsers"
                        id="shellUsers"
                        render={(arrayHelpers) => {
                          ArrayHelperRef.current = arrayHelpers
                          return(
                            shellUsers.map((item, id) => (
                              <div className={classes.wrapper} key={id}>
                                <div className={classes.inputContainer}>
                                  <label className={`${classes.label} ${id !== 0  ? classes.hideLabel : ""}`}>
                                    <FormattedMessage id="inviteauthorisedmodal.name"/>
                                  </label>
                                  <div className={classes.inputText}>
                                    {item?.name}
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id="tooltipRole" className={`${general.main} ${classes.tooltip}`}>
                                          <div className={classes.tooltipTitle}>
                                            <FormattedMessage id="inviteauthorisedmodal.tooltip.title" values={{dateOfBirth: `${moment(item?.birthdate, "YYYYMMDD").locale(globalLocale).format("MMM D")}`}}/>
                                          </div>
                                          <div className={classes.tooltipDescription}>
                                            <FormattedMessage id="inviteauthorisedmodal.tooltip.description" />
                                          </div>
                                        </Tooltip>
                                      }
                                    >
                                      <HelpIcon className={classes.helpIcon}/>
                                    </OverlayTrigger>
                                  </div>
                                </div>
                                <div className={classes.inputWrapper}>
                                  <div className={classes.inputAndBtnContainer}>
                                    <label htmlFor={`shellUsers.${id}.email`} className={`${classes.label} ${id !== 0  ? classes.hideLabel : ""}`}>
                                      <FormattedMessage id="inviteauthorisedmodal.email"/>
                                    </label>
                                    <Field
                                      placeholder={intl.formatMessage(messages.emailPlaceholderMessage)}
                                      name={`shellUsers.${id}.email`}
                                      id={`shellUsers.${id}.email`}
                                      className={
                                        getIn(errors, `shellUsers.${id}.email`) && getIn(touched, `shellUsers.${id}.email`)
                                          ? 'input-error'
                                          : null
                                      }
                                    />
                                    <ErrorMessage
                                      name={`shellUsers.${id}.email`}
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                  <UCButton
                                    className={`${classes.deleteBtn} ${id === 0  ? classes.firstBtn : ""}`}
                                    onClick={() => {
                                      userContext?.manageCompany ?
                                        handleDeleteTeam(item, id) :
                                      setShowAuDeleteWarningModal(true)
                                      setSelectedUser({...item, id: id})
                                    }}
                                    type="default"
                                    icon={<TrashIcon />}
                                  />
                                </div>
                              </div>
                            ))
                          )
                        }}
                      />
                    </Row>
                    <div className={classes.btnContainer}>
                      <Container>
                        <Row>
                          <Col className={classes.colRemove} >
                            <UCButton
                              className={classes.createBtn}
                              onClick={handleFormSubmit}
                              type="primary"
                              text={<FormattedMessage id="inviteauthorisedmodal.invite"/>}
                              disabled={values.shellUsers.every(user => user.email === "")}
                              spinner={{
                                name: "inviteAuthorisedSpinner",
                                size: 20,
                              }}
                            />
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  </form>
                </>
              )}
            </Formik>
          </div>
          : <AuthorisedSkeleton />
      }
      <AuDeleteWarningModal
        showAuDeleteWarningModal={showAuDeleteWarningModal}
        setShowAuDeleteWarningModal={setShowAuDeleteWarningModal}
        selectedUser = {selectedUser}
        handleDeleteTeam = {handleDeleteTeam}
      />
    </>
  )
}

export default Content;
